import { adaptV4Theme, createTheme } from '@mui/material'

import { Colors } from './colors'

export const sizes = {
  small: 'small',
  medium: 'medium',
  large: 'large',
  extraLarge: 'extraLarge',
} as const

export const variants = {
  primary: 'primary',
  secondary: 'secondary',
  flat: 'flat',
  text: 'text',
} as const

const defaultTheme = createTheme()

export const SPACING = 8

export const muiTheme = createTheme(
  adaptV4Theme({
    palette: {
      primary: {
        main: Colors.royalBlue,
      },
      secondary: {
        main: Colors.cyan,
      },
      background: {
        default: Colors.bgGray,
      },
      error: {
        main: Colors.errorD1,
      },
    },

    typography: {
      fontFamily: 'Overpass, sans-serif',
      fontWeightRegular: 400,
      body1: {
        fontSize: '16px',
        fontWeight: 300,
        lineHeight: '1.56',
        color: Colors.primaryBlack,
      },
      subtitle1: {
        fontSize: '20px',
        fontWeight: 300,
        color: Colors.primaryBlack,
      },
      subtitle2: {
        fontSize: '24px',
        lineHeight: '1.68',
        fontWeight: 600,
        color: Colors.primaryBlack,
      },
      h1: {
        fontSize: '60px',
        fontWeight: 600,
        lineHeight: '92px',
        color: Colors.midGray,
      },
      h5: {
        fontSize: '24px',
        lineHeight: '1.54',
        fontWeight: 600,
        color: Colors.primaryBlack,
        [defaultTheme.breakpoints.down('sm')]: {
          fontSize: '16px',
        },
      },
    },
    spacing: SPACING,

    overrides: {
      MuiContainer: {
        maxWidthLg: {
          maxWidth: 1440,
        },
      },
      MuiStepIcon: {
        root: {
          '&$completed': {
            color: Colors.cyan,
          },
          '&$active': {
            color: Colors.cyan,
          },
        },
        active: {},
        completed: {},
      },

      MuiStepLabel: {
        label: {
          fontSize: '13px',
          lineHeight: '20px',
          letterSpacing: '2px',
          fontWeight: 300,
          color: Colors.primaryBlack,
          textTransform: 'uppercase',
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: '10px',
            lineHeight: '15px',
          },
        },
      },

      MuiFormLabel: {
        root: {
          '&$focused': {
            color: Colors.primaryBlack,
          },
        },
      },

      MuiFormHelperText: {
        root: {
          '&$error': {
            fontSize: '12px',
            fontWeight: 300,
            lineHeight: '18px',
            letterSpacing: '0.02em',
          },
          color: Colors.primaryBlack,
        },
      },

      MuiTextField: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
        },
      },

      MuiFilledInput: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
        },
      },
      MuiSelect: {
        root: {
          '&.MuiSelect-select:focus': {
            backgroundColor: Colors.bgGray,
          },
        },
      },
    },
  }),
  {
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: Colors.white,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: Colors.lightGray3 },
            '&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.lightGray1,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: 'Overpass, sans-serif',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 0,
            minHeight: 44,
            minWidth: 0,
            padding: '10px 14px',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${Colors.lightGray4}`,
            boxShadow: '0 8px 24px rgba(16, 16, 19, 0.12)',
            minWidth: 200,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: 'Overpass, sans-serif',
            fontSize: 14,
            minHeight: 40,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
    },
  }
)

export const consoleTheme = createTheme({
  components: {
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          letterSpacing: '0.01071em',
        },
      },
    },
  },
})

export const colorMappings: Record<string, string> = {
  primary: muiTheme.palette.primary.main,
  secondary: muiTheme.palette.secondary.main,
}
